<script setup lang="ts" generic="T extends AnyItem">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { ComponentPublicInstance } from 'vue';
import type { AnyItem, GlobalCollectionId } from '#layers/data-model/public';
import type { BadgeProps, ButtonProps } from '#layers/ui/public';
import type { RelatedItemState } from './related-item-state';
import { computed, nextTick, onMounted, ref, useTemplateRef, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAppConfig } from '#app/config';
import RenderCollectionDisplayTemplate from '#layers/blocks/components/Render/RenderCollectionDisplayTemplate.vue';
import { useRelatedItemRoutes } from '#layers/blocks/composables/relation/use-related-item-routes';
import { useRelatedListChrome } from '#layers/blocks/composables/relation/use-related-list-chrome';
import { useSkeletonItems } from '#layers/blocks/composables/relation/use-skeleton-items';
import { CollectionReads } from '#layers/data-model/public';
import { ButtonAdd, ButtonDestructive, ButtonEdit, ButtonRemove, ListCard, OverflowMenu, useIsNarrowView, useTouchScreen, WithScope } from '#layers/ui/public';
import { RelatedItemStates } from './related-item-state';

export interface RelatedDisplayItem<TData = AnyItem> {
	id: string;
	data: TData;
	state: RelatedItemState;
	/**
	 * Whether the related record already exists on the server. Distinct from `state`: an in-form
	 * upload badges as `created` while its file record is already persisted.
	 */
	persisted: boolean;
}

export interface RelatedItemListCardPermissions<TData = AnyItem> {
	canConnect?: boolean;
	canCreate?: boolean;
	// Per-item edit gate, evaluated against the row's data item. Editing is unavailable when absent:
	// whether a row can be edited depends on the write that would carry it, which only the owner knows.
	canEditItem?: (item: TData) => boolean;
	canDisconnect?: boolean;
	// Per-item disconnect gate, evaluated against the row's data item. When set it
	// decides removal per row (e.g. scoped assign grants); when absent the card falls
	// back to the `canDisconnect` boolean.
	canDisconnectItem?: (item: TData) => boolean;
	connectDisabledReason?: string;
	createDisabledReason?: string;
}

interface RelatedItemListCardProps {
	items: RelatedDisplayItem<T>[];
	collectionId: GlobalCollectionId;
	disabled?: boolean;
	disabledNotice?: string;
	readonly?: boolean;
	showEdit?: boolean;
	showCreate?: boolean;
	showAddExisting?: boolean;
	createActionProps?: Partial<ButtonProps>;
	/** Pluralizable i18n key used for the item-count header (e.g. `common.n_files`). */
	countLabel?: string;
	pagination?: {
		page: number;
		pageSize: number;
		totalItems?: number;
		currentItems?: number;
		/**
		 * How many of the counted items are unsaved rows appended to page one. They are counted but
		 * belong to no server page, so they must not extend the page controls.
		 */
		unpagedItems?: number;
	};
	loading?: boolean;
	/**
	 * Identity of the list across remounts (e.g. the relation field id). A card re-created under the
	 * same key keeps the header and page controls its predecessor settled on during its first load.
	 * This prevents flickering of "No items" etc.
	 */
	stateKey?: string;
	permissions?: RelatedItemListCardPermissions<T>;
}

interface RelatedItemListCardEmits {
	'select': [];
	'create': [];
	'edit': [item: T];
	'remove': [item: T];
	'undo': [item: T];
	'update:page': [page: number];
	'update:pageSize': [size: number];
}

interface ListCardExpose {
	scrollToTop: () => void;
	scrollToBottom: () => void;
}

const props = withDefaults(defineProps<RelatedItemListCardProps>(), {
	showEdit: true,
	showCreate: true,
	showAddExisting: true,
	countLabel: 'common.n_items',
});

const emit = defineEmits<RelatedItemListCardEmits>();

defineSlots<{
	item?: (props: { item: RelatedDisplayItem<T> }) => unknown;
	actions?: () => unknown;
}>();

const permissions = computed(() => ({
	canConnect: props.permissions?.canConnect ?? true,
	canCreate: props.permissions?.canCreate ?? true,
	canDisconnect: props.permissions?.canDisconnect ?? true,
	connectDisabledReason: props.permissions?.connectDisabledReason,
	createDisabledReason: props.permissions?.createDisabledReason,
}));

// A disconnected row offers no edit.
function canEditItem(item: RelatedDisplayItem<T>) {
	if (item.state === 'disconnected') return false;
	return props.permissions?.canEditItem?.(item.data) ?? false;
}

// Disconnect is decided per row: a per-item predicate (scoped grants) takes
// precedence; otherwise the card-level `canDisconnect` boolean applies.
function canDisconnectItem(item: RelatedDisplayItem<T>) {
	if (props.permissions?.canDisconnectItem) return props.permissions.canDisconnectItem(item.data);
	return permissions.value.canDisconnect;
}

function handleItemClick(item: RelatedDisplayItem<T>) {
	if (props.loading || props.readonly || !props.showEdit || !canEditItem(item)) return;
	emit('edit', item.data);
}

function handleRemove(item: RelatedDisplayItem<T>) {
	if (props.disabled || !canDisconnectItem(item)) return;
	emit('remove', item.data);
}

const appConfig = useAppConfig();
const { t } = useI18n();

const itemRoutes = useRelatedItemRoutes(() => props.items, () => props.collectionId);

const listCard = useTemplateRef<ComponentPublicInstance & ListCardExpose>('listCard');

const showDisabledNotice = computed(() => !!props.disabled && !!props.disabledNotice);
const { showCountLabel, showPagination } = useRelatedListChrome({
	pagination: () => props.pagination,
	loading: () => props.loading,
	stateKey: () => props.stateKey,
});

const createButtonProps = computed<Partial<ButtonProps>>(() => ({
	size: 'sm',
	label: t('common.create_item'),
	disabled: props.disabled,
	...props.createActionProps,
}));

const listCardHeight = ref<number | null>(null);
const listSkeletonCount = ref(0);
const shouldDisplaySkeleton = ref(false);

const skeletonItems = useSkeletonItems<RelatedDisplayItem<T>>(listSkeletonCount);

onMounted(() => {
	watchEffect(async () => {
		if (listCard.value && props.pagination) {
			if (props.pagination.page === 1) {
				listCardHeight.value = null;
				listSkeletonCount.value = 0;
			}
			else if (props.items.length > listSkeletonCount.value) {
				await nextTick();
				listCardHeight.value = (listCard.value.$el as HTMLElement).clientHeight;
				listSkeletonCount.value = props.items.length;
			}
		}
	});
});

watch(() => props.pagination?.page, () => {
	shouldDisplaySkeleton.value = true;
	listCard.value?.scrollToTop();
});

watch(() => props.loading, (val) => {
	if (!val) {
		shouldDisplaySkeleton.value = false;
	}
});

function handlePageUpdate(page: number) {
	emit('update:page', page);
}

function handlePageSizeUpdate(size: number) {
	emit('update:pageSize', size);
}

const isTouchScreen = useTouchScreen();
const isNarrowCard = useIsNarrowView(listCard);

// Two independent reasons to collapse the row actions into a menu: a touch screen never fires the
// hover that reveals the inline buttons, and a narrow card has no room for them to expand into
// without squeezing the display template.
const showActionMenu = computed(() => isTouchScreen.value || isNarrowCard.value);

const undoLabelKeyByState: Partial<Record<RelatedItemState, string>> = {
	created: 'common.undo_create_item',
	connected: 'common.undo_select_item',
	disconnected: 'common.undo_deselect_item',
};

function getItemActions(item: RelatedDisplayItem<T>): DropdownMenuItem[] {
	const actions: DropdownMenuItem[] = [];
	const route = itemRoutes.value.get(item);

	if (route) {
		actions.push({
			label: t('common.go_to_related_item'),
			icon: 'lucide:arrow-up-right',
			to: route,
		});
	}

	// Navigation is not an edit, so it is the only action that survives readonly.
	if (props.readonly) return actions;

	if (props.showEdit && item.state !== RelatedItemStates.Disconnected) {
		actions.push({
			label: t('common.edit_item'),
			icon: appConfig.ui.icons.edit,
			disabled: props.disabled || !canEditItem(item),
			onSelect: () => emit('edit', item.data),
		});
	}

	const undoLabelKey = undoLabelKeyByState[item.state];

	if (undoLabelKey) {
		actions.push({
			label: t(undoLabelKey),
			icon: 'lucide:undo-2',
			color: item.state === RelatedItemStates.Created ? 'error' : 'neutral',
			onSelect: () => emit('undo', item.data),
		});
	}
	else {
		actions.push({
			label: t('common.deselect_item'),
			icon: 'lucide:unlink',
			color: 'error',
			disabled: props.disabled || !canDisconnectItem(item),
			onSelect: () => handleRemove(item),
		});
	}

	return actions;
}

const statusBadgePropsByState = computed<Record<RelatedItemState, Partial<BadgeProps> | null>>(() => ({
	default: null,
	created: { color: 'success', icon: appConfig.ui.icons.plus, label: t('common.created') },
	connected: { color: 'info', icon: 'lucide:link', label: t('common.connected') },
	disconnected: { color: 'error', icon: 'lucide:unlink', label: t('common.disconnected') },
	updated: { color: 'info', icon: appConfig.ui.icons.edit, label: t('common.edited') },
}));

defineExpose({
	scrollToTop: () => listCard.value?.scrollToTop(),
	scrollToBottom: () => listCard.value?.scrollToBottom(),
});
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<ListCard
			ref="listCard"
			:disabled
			:items="(loading && skeletonItems.length > 0) ? skeletonItems : items"
			class="max-h-[max(50vh,500px)]"
			:style="{
				minHeight: listCardHeight ? `${listCardHeight}px` : undefined,
			}"
			:ui="{
				item: {
					base: 'h-13 overflow-hidden',
					content: '-mx-2.5 h-full px-2.5',
				},
			}"
		>
			<template #header>
				<span v-if="showCountLabel" class="text-xs font-medium text-toned">{{ t(countLabel, items.length) }}</span>
			</template>
			<template #header-actions>
				<div v-if="!readonly && (showAddExisting || showCreate || !!$slots.actions)" class="flex items-center gap-2">
					<slot name="actions" />
					<DTooltip
						v-if="showAddExisting"
						:disabled="disabled || permissions.canConnect"
						:text="permissions.connectDisabledReason ?? t('permissions.no_connect_access')"
					>
						<DButton
							data-testid="add-existing-button"
							size="sm"
							variant="outline"
							color="neutral"
							leading-icon="lucide:link"
							:label="t('common.add_existing_item')"
							:disabled="disabled || !permissions.canConnect"
							:ui="{ label: 'sr-only @sm:not-sr-only' }"
							@click="emit('select')"
						/>
					</DTooltip>
					<DTooltip
						v-if="showCreate"
						:disabled="disabled || permissions.canCreate"
						:text="permissions.createDisabledReason ?? t('permissions.no_create_access')"
					>
						<ButtonAdd
							data-testid="create-button"
							v-bind="createButtonProps"
							:disabled="disabled || !permissions.canCreate"
							:ui="{ label: 'sr-only @sm:not-sr-only' }"
							@click="emit('create')"
						/>
					</DTooltip>
				</div>
			</template>
			<template #item-content="{ item }">
				<div v-if="loading && shouldDisplaySkeleton" class="animate-[fade-in_600ms_ease-out_400ms_both]">
					<DSkeleton class="h-full w-50 rounded-sm" />
				</div>
				<slot v-else name="item" :item>
					<RenderCollectionDisplayTemplate
						class="h-full text-sm"
						:collection-id
						:item="item.data"
						:read="CollectionReads.Many"
						@click="handleItemClick(item)"
					/>
				</slot>
			</template>
			<template #item-trailing="{ item }">
				<WithScope
					v-slot="{ badgeProps, itemRoute }"
					:define="{ badgeProps: statusBadgePropsByState[item.state ?? RelatedItemStates.Default], itemRoute: itemRoutes.get(item) }"
				>
					<div class="flex items-center gap-2">
						<DBadge
							v-if="badgeProps"
							data-testid="state-badge"
							:data-state="item.state"
							v-bind="badgeProps"
							class="bg-default"
							variant="outline-neutral"
							size="sm"
						/>
						<!-- A readonly row offers nothing unless its record can be opened. -->
						<template v-if="!loading && (itemRoute || !readonly)">
							<OverflowMenu v-if="showActionMenu" :items="getItemActions(item)" :trigger="{ size: 'sm' }" />
							<div
								v-else
								class="
									-ms-2 flex w-0 min-w-0 justify-end gap-1 overflow-x-clip opacity-0 transition-all duration-50
									*:shrink-0
									group-focus-within/list-item:ms-0 group-focus-within/list-item:w-auto group-focus-within/list-item:opacity-100
									group-hover/list-item:ms-0 group-hover/list-item:w-auto group-hover/list-item:opacity-100
									[&_a]:focus-visible:ring-inset
									[&_button]:focus-visible:ring-inset
								"
							>
								<DTooltip v-if="itemRoute" :text="t('common.go_to_related_item')">
									<DButton
										data-testid="navigate-button"
										icon="lucide:square-arrow-out-up-right"
										color="neutral"
										variant="ghost"
										size="sm"
										:to="itemRoute"
										:aria-label="t('common.go_to_related_item')"
									/>
								</DTooltip>
								<template v-if="!readonly">
									<ButtonEdit
										v-if="showEdit && item.state !== RelatedItemStates.Disconnected"
										class="text-default hover:text-highlighted"
										size="sm"
										:disabled="!canEditItem(item) || disabled"
										:tooltip="canEditItem(item) ? t('common.edit_item') : t('permissions.no_update_access')"
										@click="emit('edit', item.data)"
									/>
									<DTooltip v-if="item.state === RelatedItemStates.Created" :text="t('common.undo_create_item')">
										<ButtonDestructive
											data-testid="undo-button"
											icon="lucide:undo-2"
											class="p-1.5 text-muted"
											size="sm"
											@click="emit('undo', item.data)"
										/>
									</DTooltip>
									<DTooltip v-else-if="item.state === RelatedItemStates.Connected" :text="t('common.undo_select_item')">
										<DButton
											data-testid="undo-button"
											icon="lucide:undo-2"
											class="text-muted"
											color="neutral"
											variant="ghost"
											size="sm"
											@click="emit('undo', item.data)"
										/>
									</DTooltip>
									<DTooltip v-else-if="item.state === RelatedItemStates.Disconnected" :text="t('common.undo_deselect_item')">
										<DButton
											data-testid="undo-button"
											icon="lucide:undo-2"
											class="text-muted"
											color="neutral"
											variant="ghost"
											size="sm"
											@click="emit('undo', item.data)"
										/>
									</DTooltip>
									<DTooltip v-else :text="canDisconnectItem(item) ? t('common.deselect_item') : t('permissions.no_disconnect_access')">
										<ButtonRemove
											data-testid="remove-button"
											with-leading-icon
											:trash-bin="false"
											:disabled="disabled || !canDisconnectItem(item)"
											square
											@click="handleRemove(item)"
										/>
									</DTooltip>
								</template>
							</div>
						</template>
					</div>
				</WithScope>
			</template>
			<template #footer>
				<DPaginationBar
					v-if="showPagination && pagination"
					:page="pagination.page"
					:page-size="pagination.pageSize"
					:current-items="pagination.currentItems"
					:total-items="pagination.totalItems"
					:unpaged-items="pagination.unpagedItems"
					:loading
					@update:page="handlePageUpdate"
					@update:page-size="handlePageSizeUpdate"
				/>
			</template>
		</ListCard>
	</DTooltip>
</template>
